// <auto-generated>
// This file is automatically generated from DSL.
// Source: dsl/entities/Contract.yaml
// Generated: 2026-10-19T08:01:54.228Z
// </auto-generated>

import { useQuery, useMutation, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { contractService } from '@/services/forestry/ContractService';
import { CreateContract, UpdateContract, Contract } from '@/types/forestry/Contract';
import { authService } from '@/services/auth/authService';

/**
 * Query key factory for Contract
 * Includes tenant ID to ensure different tenants have separate caches
 */
export const contractKeys = {
  all: (tenantId?: string | null) => ['contracts', tenantId] as const,
  lists: (tenantId?: string | null) => [...contractKeys.all(tenantId), 'list'] as const,
  list: (tenantId?: string | null, filters?: any) => [...contractKeys.lists(tenantId), { filters }] as const,
  details: (tenantId?: string | null) => [...contractKeys.all(tenantId), 'detail'] as const,
  detail: (id: string, tenantId?: string | null) => [...contractKeys.details(tenantId), id] as const,
};

/**
 * Hook to fetch all Contracts
 */
export function useContracts(options?: Omit<UseQueryOptions<Contract[], Error>, 'queryKey' | 'queryFn'>) {
  // Get current tenant ID for cache key
  const tenantId = authService.getTenantId();

  return useQuery<Contract[], Error>({
    queryKey: contractKeys.lists(tenantId),
    queryFn: () => contractService.getAll(),
    ...options
  });
}

/**
 * Hook to fetch a single Contract by ID
 */
export function useContract(
  id: string,
  options?: Omit<UseQueryOptions<Contract | undefined, Error>, 'queryKey' | 'queryFn'>
) {
  const tenantId = authService.getTenantId();

  return useQuery<Contract | undefined, Error>({
    queryKey: contractKeys.detail(id, tenantId),
    queryFn: () => contractService.getById(id),
    enabled: !!id && id !== 'new',
    ...options
  });
}

/**
 * Hook to create a new Contract
 */
export function useCreateContract() {
  const queryClient = useQueryClient();
  const tenantId = authService.getTenantId();

  return useMutation<Contract, Error, CreateContract, { previousContracts?: Contract[] }>({
    mutationFn: (data: CreateContract) => contractService.create(data),
    onMutate: async (newContract) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: contractKeys.lists(tenantId) });

      // Snapshot previous value
      const previousContracts = queryClient.getQueryData<Contract[]>(contractKeys.lists(tenantId));

      // Optimistically update to the new value. Spread the input first so a
      // client-supplied id (offline / idempotency key) wins over the temp one.
      queryClient.setQueryData<Contract[]>(contractKeys.lists(tenantId), (old = []) => [
        ...old,
        { ...newContract, id: (newContract as { id?: string }).id ?? 'temp-' + Date.now() } as Contract
      ]);

      return { previousContracts };
    },
    onError: (_err, _newContract, context) => {
      // Rollback on error
      if (context?.previousContracts) {
        queryClient.setQueryData(contractKeys.lists(tenantId), context.previousContracts);
      }
    },
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: contractKeys.lists(tenantId) });
    }
  });
}

/**
 * Hook to update an existing Contract
 */
export function useUpdateContract() {
  const queryClient = useQueryClient();
  const tenantId = authService.getTenantId();

  return useMutation<Contract, Error, { id: string; data: UpdateContract }, { previousContract?: Contract; previousContracts?: Contract[] }>({
    mutationFn: ({ id, data }) => contractService.update(id, data),
    onMutate: async ({ id, data }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: contractKeys.detail(id, tenantId) });
      await queryClient.cancelQueries({ queryKey: contractKeys.lists(tenantId) });

      // Snapshot previous values
      const previousContract = queryClient.getQueryData<Contract>(contractKeys.detail(id, tenantId));
      const previousContracts = queryClient.getQueryData<Contract[]>(contractKeys.lists(tenantId));

      // Optimistically update detail
      queryClient.setQueryData<Contract>(contractKeys.detail(id, tenantId), (old) => ({
        ...old!,
        ...data
      }) as Contract);

      // Optimistically update list
      queryClient.setQueryData<Contract[]>(contractKeys.lists(tenantId), (old = []) =>
        old.map((item) => (item.id === id ? { ...item, ...data } as Contract : item))
      );

      return { previousContract, previousContracts };
    },
    onError: (_err, { id }, context) => {
      // Rollback on error
      if (context?.previousContract) {
        queryClient.setQueryData(contractKeys.detail(id, tenantId), context.previousContract);
      }
      if (context?.previousContracts) {
        queryClient.setQueryData(contractKeys.lists(tenantId), context.previousContracts);
      }
    },
    onSuccess: (_, { id }) => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: contractKeys.detail(id, tenantId) });
      queryClient.invalidateQueries({ queryKey: contractKeys.lists(tenantId) });
    }
  });
}

/**
 * Hook to delete a Contract
 */
export function useDeleteContract() {
  const queryClient = useQueryClient();
  const tenantId = authService.getTenantId();

  return useMutation<void, Error, string, { previousContracts?: Contract[] }>({
    mutationFn: (id: string) => contractService.delete(id),
    onMutate: async (id) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: contractKeys.lists(tenantId) });

      // Snapshot previous value
      const previousContracts = queryClient.getQueryData<Contract[]>(contractKeys.lists(tenantId));

      // Optimistically remove from list
      queryClient.setQueryData<Contract[]>(contractKeys.lists(tenantId), (old = []) =>
        old.filter((item) => item.id !== id)
      );

      return { previousContracts };
    },
    onError: (_err, _id, context) => {
      // Rollback on error
      if (context?.previousContracts) {
        queryClient.setQueryData(contractKeys.lists(tenantId), context.previousContracts);
      }
    },
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: contractKeys.lists(tenantId) });
    }
  });
}

/**
 * Combined hook for save (create or update)
 * Automatically determines whether to create or update based on presence of ID
 */
export function useSaveContract() {
  const createMutation = useCreateContract();
  const updateMutation = useUpdateContract();

  return {
    mutate: (params: { id?: string; data: CreateContract | UpdateContract }, options?: any) => {
      if (params.id && params.id !== 'new') {
        return updateMutation.mutate({ id: params.id, data: params.data as UpdateContract }, options);
      } else {
        return createMutation.mutate(params.data as CreateContract, options);
      }
    },
    isPending: createMutation.isPending || updateMutation.isPending,
    isError: createMutation.isError || updateMutation.isError,
    error: createMutation.error || updateMutation.error
  };
}
